// AuthContext.js - Authentication context for Travel+ CRM
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // `silent` refreshes the user in place; without it the loading flag unmounts the whole protected page (losing form state/messages)
  const checkAuth = async ({ silent = false } = {}) => {
    console.log('🔍 AuthContext checkAuth - Starting authentication check');
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 AuthContext checkAuth - Token exists:', !!token);
      console.log('🔍 AuthContext checkAuth - Token value:', token ? token.substring(0, 20) + '...' : 'null');
      
      if (token) {
        console.log('🔍 AuthContext checkAuth - Calling authService.getProfile()');
        const userData = await authService.getProfile();
        console.log('🔍 AuthContext checkAuth - Profile response:', userData);
        
        const user = userData.user || userData.data || userData;
        console.log('🔍 AuthContext checkAuth - Extracted user:', user);
        console.log('🔍 AuthContext checkAuth - User role:', user?.role);
        console.log('🔍 AuthContext checkAuth - User phone from /auth/profile:', user?.phone);
        
        // If user is an agent or independent agent, get the detailed profile data with firstName/lastName and commission_percentage
        if (user.role === 'agent' || user.role === 'independent_agent') {
          console.log('🔍 AuthContext checkAuth - User is agent/independent_agent, fetching detailed profile');
          try {
            const agentProfile = await authService.getAgentProfile();
            console.log('🔍 AuthContext checkAuth - Agent profile:', agentProfile);
            setUser(agentProfile);
            console.log('✅ AuthContext checkAuth - Agent user set successfully');
          } catch (agentError) {
            // If agent profile fails, fall back to basic user data
            console.warn('⚠️ Failed to fetch agent profile, using basic user data:', agentError);
            setUser(user);
            console.log('✅ AuthContext checkAuth - Basic user set as fallback');
          }
        } else {
          console.log('🔍 AuthContext checkAuth - User is not agent, setting basic user data');
          setUser(user);
          console.log('✅ AuthContext checkAuth - User set successfully:', user);
        }
      } else {
        console.log('🔍 AuthContext checkAuth - No token found, user remains null');
      }
    } catch (error) {
      console.error('❌ AuthContext checkAuth - Error occurred:', error);
      console.error('❌ AuthContext checkAuth - Error response:', error.response);
      console.error('❌ AuthContext checkAuth - Error status:', error.response?.status);
      console.error('❌ AuthContext checkAuth - Error data:', error.response?.data);
      
      // Don't log error for 401s - this is expected for public pages
      if (error.response?.status !== 401) {
        console.error('Auth check failed:', error);
      }
      // Only remove token if it's actually invalid (not just a network error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🔍 AuthContext checkAuth - Removing invalid token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        console.log('🔍 AuthContext checkAuth - Token and user cleared');
      }
    } finally {
      console.log('🔍 AuthContext checkAuth - Setting loading to false');
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.login(credentials);
      // authService.login already sets the token in localStorage
      
      // Extract user data from the API response structure
      // Backend returns: { success, data: { token, user }, message, timestamp }
      const userData = response.data?.data?.user || response.data?.user || response.user;
      console.log(' AuthContext login - Extracted user data:', userData);
      
      setUser(userData);
      console.log(' AuthContext login - User set successfully:', userData);
      return response;
    } catch (error) {
      console.error(' AuthContext login - Login failed:', error);
      setError(error.message || 'Login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log(' AuthContext logout - Starting logout process');
      await authService.logout();
    } catch (error) {
      console.error(' AuthContext logout - Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      console.log(' AuthContext logout - Token removed, user set to null');
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    setUser,
    isAuthenticated: !!user,
    isAgent: user?.role === 'agent',
    isAgencyAdmin: user?.role === 'agency_admin',
    isSuperAdmin: user?.role === 'super_admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
